<template>
    <div class="notifications-drawer" data-testid="notifications-drawer">
        <Drawer placement="left" :transfer="false" :model-value="isVisible" width="400px" @onClose="close">
            <div class="notifications-drawer__header">
                <div class="notifications-drawer__header-title">Notifications</div>
                <p class="notifications-drawer__header-desc">
                    When the ads are ready, we'll send you the link by email, and it'll also appear here.
                </p>
                <hox-tabs color="tertiary" size="small">
                    <hox-tabs-item
                        :is-active="activeNotificationTab === 'in-progress'"
                        data-testid="notifications-drawer__navigation__tab-in-progress"
                        @click="changeNotificationsTab('in-progress')"
                    >
                        In Progress
                    </hox-tabs-item>
                    <hox-tabs-item
                        :is-active="activeNotificationTab === 'completed'"
                        data-testid="notifications-drawer__navigation__tab-completed"
                        @click="changeNotificationsTab('completed')"
                    >
                        Completed
                    </hox-tabs-item>
                </hox-tabs>
            </div>
            <div v-if="activeNotificationTab === 'in-progress'" class="notifications-drawer__content">
                <hox-alert
                    v-if="hasErrorLoadingMyJobsByStatus"
                    margin-bottom="none"
                    size="small"
                    type="danger"
                    data-testid="notifications-drawer__alert-error"
                >
                    <template #title>
                        There was an unexpected error while attempting to get your background jobs.
                    </template>
                    <template #content>
                        <p>Hopefully it is just a temporary issue and it should work if you retry in a few moments.</p>
                    </template>
                    <template #actionItems>
                        <Button
                            :loading="isLoadingMyJobsByStatus > 0"
                            type="primary"
                            data-testid="notifications-drawer__retry-button"
                            @click="runMyJobsByStatusQuery"
                        >
                            Retry
                        </Button>
                    </template>
                </hox-alert>
                <div
                    v-else-if="notifications.length === 0"
                    class="notifications-drawer__empty-message-wrapper"
                    data-testid="notifications-drawer__empty-message"
                >
                    <hox-empty-message type="all-clear">
                        <template #title>There are no notifications</template>
                    </hox-empty-message>
                </div>
                <div v-for="notification of notifications" :key="notification.id">
                    <ResourceDownloadJobNotificationItem
                        v-if="
                            notification.type === NotificationTypes.FlashtalkingDownloadExcel ||
                            notification.type === NotificationTypes.FlashtalkingDownloadAssets ||
                            notification.type === NotificationTypes.PsdDownloader ||
                            notification.type === NotificationTypes.PlainlyBatchRender ||
                            notification.type === NotificationTypes.AdobeIndesignDownload
                        "
                        :notification="notification"
                        show-abort-button
                    />
                    <DownloadNotificationItem
                        v-if="notification.type === NotificationTypes.Download"
                        :notification="notification"
                        show-abort-button
                    />
                    <DownloadJobNotificationItem
                        v-else-if="notification.type === NotificationTypes.DownloadJob"
                        :notification="notification"
                        show-abort-button
                    />
                    <PreflightNotificationItem
                        v-else-if="notification.type === NotificationTypes.Preflight"
                        :notification="notification"
                        show-abort-button
                    />
                    <PublishNotificationItem
                        v-else-if="
                            notification.type === NotificationTypes.Publish ||
                            notification.type === NotificationTypes.Unpublish
                        "
                        :notification="notification"
                        show-abort-button
                    />
                    <GenerateReportNotificationItem
                        v-else-if="notification.type === NotificationTypes.GenerateReport"
                        :notification="notification"
                    />

                    <BackgroundUpdateNotificationItem
                        v-else-if="notification.type === NotificationTypes.BackgroundUpdate"
                        :notification="notification"
                    />
                </div>
            </div>
            <Scroll :on-reach-bottom="loadMoreFinishedJobs">
                <div v-if="activeNotificationTab === 'completed'" class="notifications-drawer__content">
                    <hox-alert v-if="hasErrorLoadingMyJobsByStatus" margin-bottom="none" size="small" type="danger">
                        <template #title>
                            There was an unexpected error while attempting to get your background jobs.
                        </template>
                        <template #content>
                            <p>
                                Hopefully it is just a temporary issue and it should work if you retry in a few moments.
                            </p>
                        </template>
                        <template #actionItems>
                            <Button
                                :loading="isLoadingMyJobsByStatus > 0"
                                type="primary"
                                @click="runMyJobsByStatusQuery"
                            >
                                Retry
                            </Button>
                        </template>
                    </hox-alert>
                    <div v-else-if="finishedJobs.length === 0" class="notifications-drawer__empty-message-wrapper">
                        <hox-empty-message type="all-clear">
                            <template #title>There are no finished download jobs.</template>
                        </hox-empty-message>
                    </div>
                    <div v-for="(notification, index) of finishedJobs" :key="notification.id + index">
                        {{ notification.name }}
                        <ResourceDownloadJobNotificationItem
                            v-if="
                                notification.type === NotificationTypes.FlashtalkingDownloadExcel ||
                                notification.type === NotificationTypes.FlashtalkingDownloadAssets ||
                                notification.type === NotificationTypes.PsdDownloader ||
                                notification.type === NotificationTypes.PlainlyBatchRender ||
                                notification.type === NotificationTypes.AdobeIndesignDownload
                            "
                            :notification="notification"
                            :is-job-finished="true"
                            @notificationDeleted="changeNotificationsTab('completed')"
                        />
                        <PreflightNotificationItem
                            v-else-if="notification.type === NotificationTypes.Preflight"
                            :is-job-finished="true"
                            :notification="notification"
                            show-abort-button
                            @notificationDeleted="changeNotificationsTab('completed')"
                        />
                        <PublishNotificationItem
                            v-else-if="
                                notification.type === NotificationTypes.Publish ||
                                notification.type === NotificationTypes.Unpublish
                            "
                            :notification="notification"
                            :is-job-finished="true"
                            @notificationDeleted="changeNotificationsTab('completed')"
                        />
                        <DownloadJobNotificationItem
                            v-else-if="notification.type === NotificationTypes.DownloadJob"
                            :notification="notification"
                            :is-job-finished="true"
                            @notificationDeleted="changeNotificationsTab('completed')"
                        />
                        <GenerateReportNotificationItem
                            v-else-if="notification.type === NotificationTypes.GenerateReport"
                            :notification="notification"
                            :is-job-finished="true"
                            @notificationDeleted="changeNotificationsTab('completed')"
                        />
                        <BackgroundUpdateNotificationItem
                            v-else-if="notification.type === NotificationTypes.BackgroundUpdate"
                            :notification="notification"
                            :is-job-finished="true"
                            @notificationDeleted="changeNotificationsTab('completed')"
                        />
                    </div>
                </div>
            </Scroll>
            <div
                v-if="notifications.length > 0 && activeNotificationTab === 'in-progress'"
                class="notifications-drawer__footer"
                data-testid="notifications-drawer__in-progress__clear-all-button"
                @click="clearItemsNotPendingOrInProgress"
            >
                Clear all notifications
            </div>
            <div
                v-if="finishedJobs.length > 0 && activeNotificationTab === 'completed'"
                class="notifications-drawer__footer"
                data-testid="notifications-drawer__completed__clear-all-button"
                @click="clearAllNotifications"
            >
                Clear all notifications
            </div>
        </Drawer>
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { PLATFORM } from "hoxton-shared/enums/exports";

import jobsByIdQuery from "@/apollo/queries/v2/JobsById.gql";
import myJobsByStatusQuery from "@/apollo/queries/v2/MyJobsByStatus.gql";
import DownloadNotificationItem from "@/components/Notifications/DownloadNotificationItem.vue";
import DownloadJobNotificationItem from "@/components/Notifications/DownloadJobNotificationItem.vue";
import PublishNotificationItem from "@/components/Notifications/PublishNotificationItem.vue";
import GenerateReportNotificationItem from "@/components/Notifications/GenerateReportNotificationItem.vue";
import BackgroundUpdateNotificationItem from "@/components/Notifications/BackgroundUpdateNotificationItem.vue";
import ResourceDownloadJobNotificationItem from "@/components/Notifications/ResourceDownloadJobNotificationItem.vue";
import PreflightNotificationItem from "@/components/Notifications/PreflightNotificationItem.vue";
import { JobStatus, JobStatusToNotificationStatus, JobNames } from "@/enums/jobs";
import { NotificationTypes } from "@/enums/notifications";
import { JobsAction, type JobNotification } from "@/store/modules/jobs";
import { NotificationsAction, type Notification } from "@/store/modules/notifications";
import completedJobsQuery from "@/apollo/queries/CompletedJobs.gql";
import deleteJobs from "@/apollo/mutations/DeleteJobs.gql";

export default defineComponent({
    components: {
        BackgroundUpdateNotificationItem,
        DownloadNotificationItem,
        DownloadJobNotificationItem,
        GenerateReportNotificationItem,
        PublishNotificationItem,
        ResourceDownloadJobNotificationItem,
        PreflightNotificationItem
    },

    data() {
        return {
            hasErrorLoadingMyJobsByStatus: false,
            isLoadingMyJobsByStatus: 0,
            activeNotificationTab: "in-progress",
            finishedJobs: [] as Notification[],
            finishedJobsLimit: 0,
            finishedJobsSkip: 10,
            allowLoadingMoreFinishedJobs: true,
            isLoadingMoreFinishedJobs: false,
            NotificationTypes
        };
    },

    computed: {
        isAuthorized() {
            return this.$store.state.auth.isAuthorized;
        },

        isVisible() {
            return this.$store.state.notifications.drawerIsVisible;
        },

        jobIdsToQuery() {
            return Object.keys(this.$store.state.jobs.notificationIdsByActiveJobId);
        },

        notifications() {
            return this.$store.state.notifications.items;
        }
    },

    watch: {
        $route() {
            this.close();
        }
    },

    created() {
        this.NotificationTypes = NotificationTypes;
    },

    methods: {
        changeNotificationsTab(tab: "in-progress" | "completed") {
            this.activeNotificationTab = tab;

            if (tab === "completed") {
                this.updateCompletedJobsTab();
            }
        },

        async clearAllNotifications() {
            try {
                const response = await this.$apollo.mutate({
                    mutation: deleteJobs
                });
                this.finishedJobs = [];
                this.$snackbar.success("All notifications have been cleared");
                return response;
            } catch (error) {
                const message = error instanceof Error ? error.message : "An unexpected error occurred";
                this.$snackbar.error(message);
                throw error;
            }
        },

        clearItemsNotPendingOrInProgress() {
            this.$store.dispatch(NotificationsAction.ClearItemsNotPendingOrInProgress);
        },

        clearCompletedNotifications() {
            this.$store.dispatch(NotificationsAction.ClearCompletedItems);
        },

        close() {
            this.activeNotificationTab = "in-progress";
            this.finishedJobsLimit = 0;
            this.finishedJobsSkip = 10;
            this.allowLoadingMoreFinishedJobs = true;
            this.$store.dispatch(NotificationsAction.SetDrawerIsVisible, false);
        },

        runMyJobsByStatusQuery() {
            this.$apollo.queries.myJobsByStatus.refetch();
        },

        getSnackBarMessageByJob(job: JobNotification) {
            if (job.status === JobStatus.ABORTED) {
                return {
                    error: "Your job was successfully aborted"
                };
            }

            const jobResultMessages = {
                [JobNames.UnpublishXmlFeed]: {
                    success:
                        "The ads has been removed from feeds. You can find the updated feeds in your notifications",
                    error: job.message
                },
                [JobNames.PublishXmlFeed]: {
                    success:
                        "The ads has been published to feeds. You can find the updated feeds in your notifications",
                    error: job.message
                },
                [JobNames.AddMetadataValue]: {
                    success: "The ads has been updated",
                    error: job.message
                },
                [JobNames.RemoveMetadataValue]: {
                    success: "The ads has been updated",
                    error: job.message
                }
            };

            if (job.name === JobNames.PublishXmlFeed && job.payload.publishProvider === PLATFORM.CREATIVEX) {
                return {
                    success: "The ads have been sent to CreativeX for pre-flight analysis",
                    error: job.message
                };
            }

            if (job.name === JobNames.PublishXmlFeed && job.payload.publishProvider === PLATFORM.SATALIA) {
                return {
                    success: "The ads have been sent for pre-flight analysis",
                    error: job.message
                };
            }

            if (job.name === JobNames.PublishXmlFeed && job.payload.isRendering) {
                return {
                    success: "The ads has been rendered to feeds. You can find the updated feeds in your notifications",
                    error: job.message
                };
            }

            if (jobResultMessages[job.name as keyof typeof jobResultMessages]) {
                return jobResultMessages[job.name as keyof typeof jobResultMessages];
            }

            return {
                success: "Your ads are ready to be downloaded. You can find a download link in your notifications",
                error: "There was an unexpected error while preparing ads for download"
            };
        },
        isPublishDownloaderSubJob(job: JobNotification) {
            return (
                job.payload.publishProvider !== undefined &&
                (job.name === JobNames.ScreenshotDownloader || job.name === JobNames.VideoDownloader)
            );
        },

        getNotificationPlatform(job: JobNotification) {
            return job.payload.publishProvider ?? job.payload.exportFormat ?? job.payload.platform;
        },

        getNotificationTypeByJob(job: JobNotification) {
            const publishProvider = job.payload.publishProvider;
            const isPreflightPlatform = publishProvider === PLATFORM.CREATIVEX || publishProvider === PLATFORM.SATALIA;
            const isPublishDownloaderSubJob = this.isPublishDownloaderSubJob(job);
            const jobNotificationMap = {
                [JobNames.UnpublishXmlFeed]: NotificationTypes.Unpublish,
                [JobNames.PublishXmlFeed]: isPreflightPlatform
                    ? NotificationTypes.Preflight
                    : NotificationTypes.Publish,
                [JobNames.ScreenshotDownloader]: isPreflightPlatform
                    ? NotificationTypes.Preflight
                    : isPublishDownloaderSubJob
                      ? NotificationTypes.BackgroundUpdate
                      : NotificationTypes.DownloadJob,
                [JobNames.VideoDownloader]: isPreflightPlatform
                    ? NotificationTypes.Preflight
                    : isPublishDownloaderSubJob
                      ? NotificationTypes.BackgroundUpdate
                      : NotificationTypes.DownloadJob,
                [JobNames.AddMetadataValue]: NotificationTypes.BackgroundUpdate,
                [JobNames.RemoveMetadataValue]: NotificationTypes.BackgroundUpdate,
                [JobNames.PUBFT_DOWNLOAD_EXCEL]: NotificationTypes.FlashtalkingDownloadExcel,
                [JobNames.PUBFT_DOWNLOAD_ASSETS]: NotificationTypes.FlashtalkingDownloadAssets,
                [JobNames.PsdDownloader]: NotificationTypes.PsdDownloader,
                [JobNames.PlainlyBatchRender]: NotificationTypes.PlainlyBatchRender,

                [JobNames.ADOBE_INDESIGN_DOWNLOAD]: NotificationTypes.AdobeIndesignDownload
            };

            return jobNotificationMap[job.name as keyof typeof jobNotificationMap] || NotificationTypes.DownloadJob;
        },

        loadMoreFinishedJobs() {
            if (!this.isLoadingMoreFinishedJobs && this.allowLoadingMoreFinishedJobs) {
                this.isLoadingMoreFinishedJobs = true;
                this.finishedJobsLimit += 1;
                this.$apollo.queries.completedJobs.refetch();
            }
        },

        getJobCompletionFlags(job: JobNotification, notificationType: string) {
            const hasDownloadLink = !!(job.zipUrl ?? job.payload.zipUrl);
            const isDownloadNotification =
                notificationType === NotificationTypes.DownloadJob ||
                notificationType === NotificationTypes.FlashtalkingDownloadExcel ||
                notificationType === NotificationTypes.FlashtalkingDownloadAssets ||
                notificationType === NotificationTypes.PsdDownloader ||
                notificationType === NotificationTypes.PlainlyBatchRender;
            const isResourceManagerDownloadNotification =
                notificationType === NotificationTypes.FlashtalkingDownloadExcel ||
                notificationType === NotificationTypes.FlashtalkingDownloadAssets ||
                notificationType === NotificationTypes.PlainlyBatchRender ||
                notificationType === NotificationTypes.PsdDownloader;
            const requiresDownloadLink = isDownloadNotification && !isResourceManagerDownloadNotification;
            return {
                hasDownloadLink,
                isDownloadNotification,
                isResourceManagerDownloadNotification,
                requiresDownloadLink,
                isFailure: job.status === JobStatus.FAILED || job.status === JobStatus.ABORTED,
                isCompletedWithLink: requiresDownloadLink && job.status === JobStatus.COMPLETE && hasDownloadLink,
                isCompletedResourceManagerDownload:
                    isResourceManagerDownloadNotification && job.status === JobStatus.COMPLETE,
                isCompletedWithoutLinkAndNotDownload: !isDownloadNotification && job.status === JobStatus.COMPLETE
            };
        },

        updateCompletedJobsTab() {
            this.isLoadingMoreFinishedJobs = true;
            this.finishedJobs = [];
            this.finishedJobsLimit = 0;
            this.allowLoadingMoreFinishedJobs = true;
            this.$apollo.queries.completedJobs.refetch();
        }
    },

    apollo: {
        myJobsByStatus: {
            query: myJobsByStatusQuery,
            fetchPolicy: "no-cache",
            loadingKey: "isLoadingMyJobsByStatus",
            skip() {
                return !this.isAuthorized;
            },
            variables() {
                return {
                    status: [JobStatus.PENDING, JobStatus.IN_PROGRESS]
                };
            },
            async result({ data, error }) {
                if (error) {
                    this.hasErrorLoadingMyJobsByStatus = true;
                    return;
                }
                this.hasErrorLoadingMyJobsByStatus = false;
                const notificationIdsWithJobIds = await Promise.all(
                    data.myJobsByStatus
                        .filter((job: JobNotification) => !this.isPublishDownloaderSubJob(job))
                        .map(async (job: JobNotification) => {
                            /*
                            In theory we should never have a notification for any job at this point.
                            However, vue-apollo is an interesting beast that occasionally does unexpected
                            things so we're going to err on the side of caution and do a check so that we
                            do not end up with multiple notifications per job.
                        */
                            let notificationId = this.$store.state.jobs.notificationIdsByActiveJobId[job._id];
                            if (notificationId === undefined) {
                                notificationId = await this.$store.dispatch(NotificationsAction.Add, {
                                    message: job.message,
                                    status: JobStatusToNotificationStatus[job.status],
                                    type: this.getNotificationTypeByJob(job),
                                    job,
                                    ...(this.getNotificationPlatform(job) && {
                                        platform: this.getNotificationPlatform(job)
                                    }),
                                    ...(job.payload?.publishingAdsCount && {
                                        deliverablesCount: job.payload.publishingAdsCount
                                    })
                                });
                            }
                            return {
                                jobId: job._id,
                                notificationId
                            };
                        })
                );
                notificationIdsWithJobIds.forEach(notificationIdWithJobId => {
                    this.$store.dispatch(JobsAction.SetNotificationIdByActiveJobId, notificationIdWithJobId);
                });
            }
        },

        jobs: {
            query: jobsByIdQuery,
            fetchPolicy: "no-cache",
            pollInterval: 5000,
            skip() {
                return this.jobIdsToQuery.length === 0 || !this.isAuthorized;
            },
            variables() {
                return {
                    jobIds: this.jobIdsToQuery
                };
            },
            result({ data, error }) {
                if (error) {
                    /*
                        We don't need to do any error handling here as the request should retry
                        again in a short amount of time due to the pollInterval.
                    */
                    return;
                }

                data.jobs.forEach((job: JobNotification) => {
                    const notificationId = this.$store.state.jobs.notificationIdsByActiveJobId[job._id];
                    const isPublishDownloaderSubJob = this.isPublishDownloaderSubJob(job);

                    const notificationType = this.getNotificationTypeByJob(job);
                    const {
                        hasDownloadLink,
                        requiresDownloadLink,
                        isFailure,
                        isCompletedWithLink,
                        isCompletedResourceManagerDownload,
                        isCompletedWithoutLinkAndNotDownload
                    } = this.getJobCompletionFlags(job, notificationType);

                    const isTerminalWithoutLink =
                        requiresDownloadLink && job.status === JobStatus.COMPLETE && !hasDownloadLink;

                    const notificationStatus = isTerminalWithoutLink
                        ? JobStatusToNotificationStatus[JobStatus.IN_PROGRESS]
                        : JobStatusToNotificationStatus[job.status];

                    const notificationMessage = isTerminalWithoutLink ? "Preparing download..." : job.message;

                    this.$store.dispatch(NotificationsAction.Update, {
                        downloadLink: job.zipUrl ?? job.payload.zipUrl,
                        id: notificationId,
                        message: notificationMessage,
                        status: notificationStatus,
                        type: notificationType,
                        job,
                        ...(this.getNotificationPlatform(job) && {
                            platform: this.getNotificationPlatform(job)
                        })
                    });

                    if (
                        !isPublishDownloaderSubJob &&
                        (isCompletedWithLink ||
                            isCompletedResourceManagerDownload ||
                            isCompletedWithoutLinkAndNotDownload ||
                            isFailure)
                    ) {
                        const message = this.getSnackBarMessageByJob(job);
                        if (job.status === JobStatus.FAILED || job.status === JobStatus.ABORTED) {
                            this.$snackbar.error(message.error);
                        } else {
                            this.clearCompletedNotifications();
                            this.updateCompletedJobsTab();
                            this.$snackbar.success(message.success as string);
                        }
                        /*
                            If the job is no longer in progress or pending then we no longer want
                            to poll it for an updated status, so we remove the job reference from the
                            object that we're using to track active jobs.
                        */
                        this.$store.dispatch(JobsAction.DeleteNotificationIdByActiveJobId, job);
                    }
                });
            }
        },

        completedJobs: {
            query: completedJobsQuery,
            fetchPolicy: "no-cache",
            skip() {
                return !this.isAuthorized;
            },
            variables() {
                return {
                    limit: this.finishedJobsSkip,
                    skip: this.finishedJobsSkip * this.finishedJobsLimit
                };
            },

            result({ data, error }) {
                if (error) {
                    this.$snackbar.error("There was an unexpected error while attempting to get your background jobs.");
                    return;
                }

                if (data.completedJobs.length < this.finishedJobsSkip) {
                    this.allowLoadingMoreFinishedJobs = false;
                }

                const updatedCompleteJobs = data.completedJobs
                    .filter((job: JobNotification) => !this.isPublishDownloaderSubJob(job))
                    .map((job: JobNotification) => {
                        const formattedJob = {
                            message: job.message,
                            status: JobStatusToNotificationStatus[job.status],
                            type: this.getNotificationTypeByJob(job),
                            job: job,
                            id: job._id,
                            downloadLink: job.zipUrl ?? job.payload?.zipUrl,
                            ...(this.getNotificationPlatform(job) && {
                                platform: this.getNotificationPlatform(job)
                            }),
                            ...(job.payload?.publishingAdsCount && {
                                deliverablesCount: job.payload.publishingAdsCount
                            })
                        };

                        return formattedJob;
                    });

                this.isLoadingMoreFinishedJobs = false;
                const dedup = (items: Notification[]) => {
                    const seen = new Set<string>();
                    return items.filter(n => {
                        if (seen.has(n.id)) return false;
                        seen.add(n.id);
                        return true;
                    });
                };
                if (this.finishedJobsLimit === 0) {
                    this.finishedJobs = dedup(updatedCompleteJobs);
                } else {
                    this.finishedJobs = dedup([...this.finishedJobs, ...updatedCompleteJobs]);
                }
            }
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.notifications-drawer {
    .ivu-drawer-mask,
    .ivu-drawer-wrap {
        z-index: $zindex-notifications-drawer;
    }

    .ivu-drawer-body {
        display: flex;
        flex-direction: column;
        padding: 0;
        height: 100%;
    }

    .ivu-drawer-content {
        box-sizing: content-box;
        border-left: $campaign-vertical-nav-width solid $cmp-light-bg-color;
    }

    .ivu-scroll-wrapper {
        width: 100%;
        flex: 1;
        overflow: auto;

        .ivu-scroll-container {
            height: 100% !important;
        }
    }
}

.notifications-drawer__content {
    flex: 1 1 auto;
    overflow: auto;
    height: 100%;
}

.notifications-drawer__empty-message-wrapper {
    padding: $spacing-large;
}

.notifications-drawer__footer {
    background: $white;
    cursor: pointer;
    flex: 0;
    font-size: $font-size-base;
    font-weight: bold;
    transition: background 0.2s ease-in-out;
    text-align: center;
    margin: 10px;
    border: 1px solid var(--wpp-primary-color-500);
    padding: 10px;
    border-radius: 10px;
    text-transform: none;
    color: var(--wpp-primary-color-500);

    &:hover {
        background: $grey2;
    }
}

.notifications-drawer__header {
    border-bottom: $border-width-base solid $cmp-light-border-color;
    padding: $spacing-large 0 0 0;

    &-title {
        color: #000;
        font-size: 18px;
        font-weight: 600;
        padding: 0 15px 5px;
    }
    &-desc {
        color: #4d5358;
        font-size: 14px;
        line-height: 22px;
        padding: 0 15px 20px;
    }
}
</style>
